import { useAuth } from "@/components/auth/auth-context";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useCareReviewCount } from "@/lib/api/care";
import { useNotificationCount } from "@/lib/api/notifications";
import { useRiskFlagCount } from "@/lib/api/risk-flagging";
import { useUnreadChangelogCount } from "@/lib/api/changelog";
import { INTERNAL_OPS_CUSTOM_TYPES } from "@/lib/constants";
import { useChangelogStore } from "@/lib/hooks/use-changelog-store";
import { useFeatureFlagEnabled } from "@posthog/react";
import { PostHogEvents, useTrackEvent } from "@/lib/posthog-events";
import { useCurrentUser } from "@/lib/api/user";
import { useAbility } from "@/lib/casl/ability-context";
import { hasPrivilegedAccessFromAbility } from "@/lib/casl/privileged-access";
import { useCan } from "@/lib/casl/use-can";
import { useMentorView } from "@/lib/hooks/admin/use-mentor-view";
import { useChannelsList } from "@/lib/hooks/conversations/use-channels-list";
import { useChatWorkspaceAccess } from "@/lib/hooks/conversations/use-chat-workspace-access";
import { useEscalationNotification } from "@/lib/hooks/conversations/useEscalationNotification";
import { useInboxStore } from "@/lib/hooks/use-inbox-store";
import { useIntercom } from "@/lib/hooks/use-intercom";
import {
  BellIcon,
  CalendarDaysIcon,
  Cog6ToothIcon,
  ExclamationTriangleIcon,
  HomeIcon,
  UsersIcon,
  UserGroupIcon,
  QuestionMarkCircleIcon,
  UserPlusIcon,
  CalendarIcon,
  BookOpenIcon,
  SparklesIcon,
  ChatBubbleOvalLeftIcon,
  DocumentTextIcon,
  ChartBarIcon,
  ClipboardDocumentCheckIcon,
  ClipboardIcon,
  TicketIcon,
  FlagIcon,
  DevicePhoneMobileIcon,
  ShieldCheckIcon,
  MegaphoneIcon,
  AcademicCapIcon,
} from "@heroicons/react/24/outline";
import {
  BellIcon as BellSolidIcon,
  CalendarDaysIcon as CalendarDaysSolidIcon,
  ExclamationTriangleIcon as ExclamationTriangleSolidIcon,
  HomeIcon as HomeSolidIcon,
  UsersIcon as UsersSolidIcon,
  UserGroupIcon as UserGroupSolidIcon,
  UserPlusIcon as UserPlusSolidIcon,
  BookOpenIcon as BookOpenSolidIcon,
  SparklesIcon as SparklesSolidIcon,
  ChatBubbleOvalLeftIcon as ChatBubbleOvalLeftSolidIcon,
  DocumentTextIcon as DocumentTextSolidIcon,
  ChartBarIcon as ChartBarSolidIcon,
  ClipboardDocumentCheckIcon as ClipboardDocumentCheckSolidIcon,
  ClipboardIcon as ClipboardSolidIcon,
  TicketIcon as TicketSolidIcon,
  FlagIcon as FlagSolidIcon,
  DevicePhoneMobileIcon as DevicePhoneMobileSolidIcon,
  ShieldCheckIcon as ShieldCheckSolidIcon,
  MegaphoneIcon as MegaphoneSolidIcon,
  AcademicCapIcon as AcademicCapSolidIcon,
} from "@heroicons/react/24/solid";
import { Switch, Sidebar as UISidebar } from "@somethings-ui/web";
import { UserRecordSidebarAvatar } from "@/components/user-record/user-record-sidebar-avatar";
import { useUserRecordsV2 } from "@/lib/hooks/use-user-records-v2";
import { Link, useLocation, useRouter } from "@tanstack/react-router";
import { Cog, CoinsIcon, Loader2, LogOut } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { InboxSidebar } from "./inbox/inbox";
import { isMentorIdentity } from "@/lib/utils/system-roles";

export function Sidebar() {
  useEscalationNotification();

  const { data: somethingsUser } = useCurrentUser();
  const { revokeSession } = useAuth();
  const { openMessenger } = useIntercom(somethingsUser);

  const notificationCount = useNotificationCount();
  const { data: unreadChangelogCount } = useUnreadChangelogCount();
  const openChangelogModal = useChangelogStore((s) => s.openModal);

  const can = useCan();
  const ability = useAbility();
  const hasPrivilegedAccess = hasPrivilegedAccessFromAbility(ability);

  const systemRoles = somethingsUser?.systemRoles ?? [];
  const hasMentorRole = isMentorIdentity(systemRoles);
  const hasSupervisorRole = systemRoles.includes("supervisor");
  const hasMentorCapability = hasMentorRole || hasSupervisorRole;
  const discordCommunityUrl = import.meta.env.VITE_DISCORD_COMMUNITY_URL;
  const mentorGuidebookUrl = import.meta.env.VITE_MENTOR_GUIDEBOOK_URL;

  const isRiskFlaggingEnabled = useFeatureFlagEnabled("enable-risk-flagging");
  const isSmsLifecycleEnabled = useFeatureFlagEnabled("enable-sms-lifecycle");
  const isMentorPerformanceEnabled =
    useFeatureFlagEnabled("enable-mentor-performance") || import.meta.env.DEV;

  const isTrainingSimulatorEnabled =
    useFeatureFlagEnabled("enable-training-simulator") || import.meta.env.DEV;
  const userRecordsV2 = useUserRecordsV2();
  const canReadTrainingSimulator =
    can("read", "training-simulator") ||
    can("read", "training-simulator-reviews");
  const riskFlagCount = useRiskFlagCount(
    can("read", "risk-flagging") && !!isRiskFlaggingEnabled,
  );
  const careReviewCount = useCareReviewCount(can("read", "care"));

  const router = useRouter();
  const { track } = useTrackEvent();
  const [pendingLogout, setPendingLogout] = useState(false);

  const [mentorView, setMentorView] = useMentorView((state) => [
    state.mentorView,
    state.setMentorView,
  ]);

  const { toggle: toggleInbox, isOpen: isInboxOpen } = useInboxStore();

  const {
    canSeeTeens: canSeeTeenChats,
    canSeeSupport: canReadInternalChannels,
  } = useChatWorkspaceAccess();

  const shouldShowChats = canSeeTeenChats || canReadInternalChannels;

  const chatUnreadCustomTypes = useMemo(() => {
    const types: string[] = [];
    if (canSeeTeenChats) types.push("MENTORSHIP");
    if (canReadInternalChannels) types.push(...INTERNAL_OPS_CUSTOM_TYPES);
    return types;
  }, [canSeeTeenChats, canReadInternalChannels]);

  const { channels: chatChannels } = useChannelsList({
    customTypesFilter: chatUnreadCustomTypes,
    enabled: shouldShowChats,
  });
  const chatUnreadCount = useMemo(
    () => chatChannels.filter((c) => c.unreadMessageCount > 0).length,
    [chatChannels],
  );

  const handleLogout = useCallback(async () => {
    setPendingLogout(true);
    await revokeSession();
    setPendingLogout(false);
    setTimeout(() => {
      router.navigate({
        to: "/login",
      });
    }, 1000);
  }, [revokeSession, router]);

  const { pathname } = useLocation();

  const activePath = useMemo<string>(() => {
    if (isInboxOpen) return "todos";
    if (pathname === "/" || pathname.includes("/mentorships")) return "home";

    return pathname.split("/")[1] || "home";
  }, [pathname, isInboxOpen]);

  const getItems = useCallback(() => {
    const showStaffItems = !mentorView;

    return [
      {
        id: "home",
        icon: <HomeIcon className="size-6" />,
        filledIcon: <HomeSolidIcon className="size-6" />,
        title: "Home",
        isActive: activePath === "home",
        onClick: () => router.navigate({ to: "/" }),
      },
      shouldShowChats
        ? {
            id: "conversations",
            icon: <ChatBubbleOvalLeftIcon className="size-6" />,
            filledIcon: <ChatBubbleOvalLeftSolidIcon className="size-6" />,
            title: "Chats",
            isActive: activePath === "conversations",
            onClick: () => router.navigate({ to: "/conversations" }),
            badge: chatUnreadCount || undefined,
          }
        : null,
      {
        id: "todos",
        icon: <BellIcon className="size-6" />,
        filledIcon: <BellSolidIcon className="size-6" />,
        title: "Todos",
        isActive: activePath === "todos",
        onClick: toggleInbox,
        badge: notificationCount || undefined,
      },
      showStaffItems && can("read", "escalations")
        ? {
            id: "escalations",
            icon: <ExclamationTriangleIcon className="size-6" />,
            filledIcon: <ExclamationTriangleSolidIcon className="size-6" />,
            title: "Escalations",
            isActive: activePath === "escalations",
            onClick: () => router.navigate({ to: "/escalations" }),
          }
        : null,
      showStaffItems && can("read", "risk-flagging") && isRiskFlaggingEnabled
        ? {
            id: "risk-flagging",
            icon: <FlagIcon className="size-6" />,
            filledIcon: <FlagSolidIcon className="size-6" />,
            title: "Risk Flagging",
            isActive: activePath === "risk-flagging",
            onClick: () => router.navigate({ to: "/risk-flagging" }),
            badge: riskFlagCount || undefined,
          }
        : null,
      showStaffItems &&
      can("read", "care") &&
      (hasPrivilegedAccess || careReviewCount.visible !== false)
        ? {
            id: "care-reviews",
            icon: <ClipboardDocumentCheckIcon className="size-6" />,
            filledIcon: <ClipboardDocumentCheckSolidIcon className="size-6" />,
            title: "Care Reviews",
            isActive: activePath === "care-reviews",
            onClick: () => router.navigate({ to: "/care-reviews" }),
            badge: careReviewCount.open || undefined,
          }
        : null,
      showStaffItems && can("read", "users")
        ? {
            id: "users",
            icon: <UsersIcon className="size-6" />,
            filledIcon: <UsersSolidIcon className="size-6" />,
            title: "Users",
            isActive: activePath === "users",
            onClick: () => router.navigate({ to: "/users" }),
          }
        : null,
      can("read", "mentor-slots")
        ? {
            id: "mentor-slots",
            icon: <CalendarDaysIcon className="size-6" />,
            filledIcon: <CalendarDaysSolidIcon className="size-6" />,
            title: "Mentor Matching",
            isActive: activePath === "mentors",
            onClick: () => router.navigate({ to: "/mentors" }),
          }
        : null,
      isMentorPerformanceEnabled && can("read", "mentor-metrics-dashboard")
        ? {
            id: "mentor-performance",
            icon: <ClipboardIcon className="size-6" />,
            filledIcon: <ClipboardSolidIcon className="size-6" />,
            title: "Mentor Performance",
            isActive: activePath === "mentor-performance",
            onClick: () => router.navigate({ to: "/mentor-performance" }),
          }
        : null,
      {
        id: "upcoming",
        icon: <CalendarIcon className="size-6" />,
        filledIcon: <CalendarDaysSolidIcon className="size-6" />,
        title: "Upcoming",
        isActive: activePath === "upcoming",
        onClick: () => router.navigate({ to: "/upcoming" }),
      },
      {
        id: "community",
        icon: <UserGroupIcon className="size-6" />,
        filledIcon: <UserGroupSolidIcon className="size-6" />,
        title: "Community",
        isActive: false,
        onClick: () => {
          if (!discordCommunityUrl) return;
          window.open(discordCommunityUrl, "_blank", "noopener,noreferrer");
        },
      },
      {
        id: "mentor-guidebook",
        icon: <BookOpenIcon className="size-6" />,
        filledIcon: <BookOpenSolidIcon className="size-6" />,
        title: "Mentor Guidebook",
        isActive: false,
        onClick: () => {
          if (!mentorGuidebookUrl) return;
          window.open(mentorGuidebookUrl, "_blank", "noopener,noreferrer");
        },
      },
      isTrainingSimulatorEnabled && canReadTrainingSimulator
        ? {
            id: "training",
            icon: <AcademicCapIcon className="size-6" />,
            filledIcon: <AcademicCapSolidIcon className="size-6" />,
            title: "Training",
            isActive: activePath === "training",
            onClick: () => {
              track(PostHogEvents.SIMULATOR_NAV_CLICK, { source: "sidebar" });
              router.navigate({ to: "/training" });
            },
          }
        : null,
      showStaffItems && can("read", "referrals")
        ? {
            id: "referrals",
            icon: <DocumentTextIcon className="size-6" />,
            filledIcon: <DocumentTextSolidIcon className="size-6" />,
            title: "Referrals",
            isActive: activePath === "referrals",
            onClick: () => router.navigate({ to: "/referrals" }),
          }
        : null,
      can("read", "ava") && showStaffItems
        ? {
            id: "ava",
            icon: <SparklesIcon className="size-6" />,
            filledIcon: <SparklesSolidIcon className="size-6" />,
            title: "AVA",
            isActive: activePath === "ava",
            onClick: () => router.navigate({ to: "/ava" }),
          }
        : null,
      showStaffItems && can("manage", "all")
        ? {
            id: "onboarding",
            icon: <UserPlusIcon className="size-6" />,
            filledIcon: <UserPlusSolidIcon className="size-6" />,
            title: "Onboarding",
            isActive: activePath === "onboarding",
            onClick: () => router.navigate({ to: "/onboarding" }),
          }
        : null,
      showStaffItems && can("manage", "all")
        ? {
            id: "knowledge-base",
            icon: <BookOpenIcon className="size-6" />,
            filledIcon: <BookOpenSolidIcon className="size-6" />,
            title: "Knowledge Base",
            isActive: activePath === "knowledge-base",
            onClick: () => router.navigate({ to: "/knowledge-base" }),
          }
        : null,
      showStaffItems && isSmsLifecycleEnabled && can("read", "sms-lifecycle")
        ? {
            id: "sms-lifecycle",
            icon: <DevicePhoneMobileIcon className="size-6" />,
            filledIcon: <DevicePhoneMobileSolidIcon className="size-6" />,
            title: "SMS Lifecycle",
            isActive: activePath === "sms-lifecycle",
            onClick: () => router.navigate({ to: "/sms-lifecycle" }),
          }
        : null,
      showStaffItems && can("read", "roles")
        ? {
            id: "roles",
            icon: <ShieldCheckIcon className="size-6" />,
            filledIcon: <ShieldCheckSolidIcon className="size-6" />,
            title: "Roles & Permissions",
            isActive: activePath === "roles",
            onClick: () => router.navigate({ to: "/roles" }),
          }
        : null,
      showStaffItems && can("read", "payouts")
        ? {
            id: "mentor-payout",
            icon: <CoinsIcon className="size-6" />,
            filledIcon: <CoinsIcon className="size-6" />,
            title: "Mentor Payout",
            isActive: activePath === "mentor-payout",
            onClick: () => router.navigate({ to: "/mentor-payout" }),
          }
        : null,
      showStaffItems && can("read", "admin-analytics")
        ? {
            id: "mentor-forecasting",
            icon: <ChartBarIcon className="size-6" />,
            filledIcon: <ChartBarSolidIcon className="size-6" />,
            title: "Mentor Forecasting",
            isActive: activePath === "mentor-forecasting",
            onClick: () => router.navigate({ to: "/mentor-forecasting" }),
          }
        : null,
      showStaffItems && can("manage", "settings")
        ? {
            id: "changelog",
            icon: <MegaphoneIcon className="size-6" />,
            filledIcon: <MegaphoneSolidIcon className="size-6" />,
            title: "Changelog",
            isActive: activePath === "changelog",
            onClick: () => router.navigate({ to: "/changelog" }),
          }
        : null,
      showStaffItems && can("read", "campaign-codes")
        ? {
            id: "campaign-codes",
            icon: <TicketIcon className="size-6" />,
            filledIcon: <TicketSolidIcon className="size-6" />,
            title: "Campaign Codes",
            isActive: activePath === "campaign-codes",
            onClick: () => router.navigate({ to: "/campaign-codes" }),
          }
        : null,
    ].filter((item): item is NonNullable<typeof item> => item !== null);
  }, [
    activePath,
    mentorView,
    notificationCount,
    isRiskFlaggingEnabled,
    riskFlagCount,
    careReviewCount,
    hasPrivilegedAccess,
    isSmsLifecycleEnabled,
    isMentorPerformanceEnabled,
    isTrainingSimulatorEnabled,
    canReadTrainingSimulator,
    can,
    shouldShowChats,
    chatUnreadCount,
    discordCommunityUrl,
    mentorGuidebookUrl,
    router,
    toggleInbox,
    track,
  ]);

  const footerComponent = (
    <div className="flex flex-col items-center gap-4 border-t p-4">
      {hasPrivilegedAccess && hasMentorCapability && !hasMentorRole && (
        <div className="flex flex-col items-center justify-between px-2">
          <div className="flex items-center gap-2">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div>
                    <Switch value={mentorView} onValueChange={setMentorView} />
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Toggle Mentor View</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </div>
      )}
      <div className="flex flex-col items-center gap-4">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                onClick={openChangelogModal}
                className="relative cursor-pointer text-gray-600 hover:text-gray-900"
                aria-label="What's new"
              >
                <MegaphoneIcon className="size-6" />
                {!!unreadChangelogCount && unreadChangelogCount > 0 && (
                  <span className="absolute -right-1 -top-1 flex size-4 items-center justify-center rounded-full bg-red-500 text-[10px] font-semibold text-white">
                    {unreadChangelogCount > 9 ? "9+" : unreadChangelogCount}
                  </span>
                )}
              </button>
            </TooltipTrigger>
            <TooltipContent>
              <p>What&apos;s new</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                onClick={openMessenger}
                className="cursor-pointer text-gray-600 hover:text-gray-900"
              >
                <QuestionMarkCircleIcon className="size-6" />
              </button>
            </TooltipTrigger>
            <TooltipContent>
              <p>Help & Support</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
        {userRecordsV2 ? null : (
          <Popover>
            <PopoverTrigger asChild>
              <Cog6ToothIcon className="size-6 cursor-pointer text-gray-600 hover:text-gray-900" />
            </PopoverTrigger>
            <PopoverContent className="z-[52] w-[200px] p-0">
              <div className="flex flex-col">
                <div className="border-b px-4 py-2">
                  <Link
                    to={"/settings"}
                    onClick={() => true}
                    className="flex flex-row items-center gap-2"
                  >
                    <Cog className="text-gray-500" size={16} />
                    <span>Settings</span>
                  </Link>
                </div>
                {!!import.meta.env.VITE_COMMIT_SHA && (
                  <div className="border-b px-4 py-2">
                    <span className="font-bold text-black">
                      {import.meta.env.VITE_COMMIT_SHA.slice(0, 7)}
                    </span>
                  </div>
                )}
                <button
                  onClick={handleLogout}
                  disabled={pendingLogout}
                  className="flex flex-row items-center gap-2 px-4 py-2"
                >
                  {pendingLogout ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <LogOut className="text-red-500" size={16} />
                  )}
                  <span className="text-red-500">Logout</span>
                </button>
              </div>
            </PopoverContent>
          </Popover>
        )}
      </div>
    </div>
  );

  return (
    <>
      <InboxSidebar />
      <UISidebar
        logo={<UserRecordSidebarAvatar />}
        items={getItems()}
        footerComponent={footerComponent}
        className="sticky top-0 z-[51] h-screen border-r shadow-none"
      />
    </>
  );
}
